import React from "react";
import { useMutation, useQuery } from "react-query";
import {
  ApiResponseData,
  FlatCatalogue,
  getItemDescendants,
  instance,
  useCatalogue,
} from "@clearabee/ui-sdk";
import { Button } from "components/Core/Button/Button";
import { Input } from "components/Core/Input/Input";
import { useTranslation } from "react-i18next";
import {
  displayErrorMessage,
  FlexGrid,
  Link,
  Message,
  theme,
  useResponsive,
  Text,
} from "@clearabee/ui-library-native";
import { useForm, Form } from "lib/hooks/useForm";
import { z } from "zod";
import { Spinner } from "components/Core/Spinner/Spinner";
import { Platform, View } from "react-native";
import { useQuestionContext } from "lib/providers/questionProvider";
import { useState } from "react";
import { postcode } from "lib/schemas";
import { FormState } from "../SkipBags.validation";
import { useAuth } from "lib/hooks/useAuth";
import { JobSelectionCard } from "components/JobSelectionCard/JobSelectionCard";

const buildProductMap = (
  data: ApiResponseData<typeof instance.catalogues.getPrepaidOrder>,
  defaultCatalogue: FlatCatalogue,
) => {
  const products = getItemDescendants(
    "PREPAIDCOLLECTIONSKIPBAGS",
    defaultCatalogue.items,
  );

  const prepaidItems = ["SMALL", "MEDIUM", "LARGE"].reduce((final, size) => {
    const qty =
      data.items?.find(({ sku }) => sku.includes(size))?.quantity || 0;
    const item = products.find(({ sku }) => sku.includes(size));
    if (qty && item) {
      return {
        ...final,
        [item.sku]: qty,
      };
    }
    return final;
  }, {} as Record<string, number>);
  return prepaidItems;
};

export const RefAndPostcode = (): React.ReactElement => {
  const [translate] = useTranslation();
  const context = useQuestionContext<FormState>();
  const { defaultCatalogue } = useCatalogue();
  const [customError, setCustomError] = useState<string>();
  const { user } = useAuth();
  const { screens } = useResponsive();
  const [manualLookup, setManualLookup] = useState(!user);

  const { data: jobs, isFetching } = useQuery(
    ["getUserJobs", user],
    async () => {
      const { data } = await instance.jobs.postQueryJobs({
        limit: 20,
        filters: [["status", "=", "Completed"]],
      });

      const skipBagOrders = data.items.filter(({ items }) =>
        items?.some(({ sku }) => sku.includes("BAGCOLLECTION")),
      );

      const prepaidResponses = await Promise.allSettled(
        skipBagOrders.map(
          async ({ ref, addressPostcode }) =>
            (
              await instance.catalogues.getPrepaidOrder(
                encodeURIComponent(ref),
                addressPostcode,
              )
            ).data,
        ),
      );

      return prepaidResponses.flatMap((res, index) =>
        res.status === "fulfilled"
          ? { prepaidOrder: res.value, job: skipBagOrders[index] }
          : [],
      );
    },
    {
      enabled: !!user,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        if (!data.length) setManualLookup(true);
      },
    },
  );

  const form = useForm({
    defaultValues: {
      ref: context.get("refAndPostcode")?.ref ?? "",
      postcode: context.get("refAndPostcode")?.postcode ?? "",
      selectedJob: undefined,
    },
    validationSchema: z.object({
      ref: manualLookup
        ? z
            .string()
            .min(1)
            .transform((value) => value.split(" ").join(""))
        : z.string(),
      postcode: manualLookup ? postcode : z.string(),
      selectedJob: manualLookup ? z.number().optional() : z.number(),
    }),
    onSubmit: async (value) => {
      if (!manualLookup && typeof value.selectedJob === "number") {
        const job = jobs?.[value.selectedJob];

        if (!job) throw Error("No job at index exists");
        context
          .set("refAndPostcode", {
            ref: job.prepaidOrder.orderRef,
            postcode: job.prepaidOrder.postcode || "",
          })
          .set(
            "products",
            buildProductMap(job.prepaidOrder, defaultCatalogue),
            null,
          )
          .set("bagContents", {
            types: [],
            notes: "",
          })
          .open("bagContents");
      } else {
        mutate(value);
      }
    },
  });

  const { mutate, error, isLoading, reset } = useMutation(
    async ({ ref, postcode }: { ref: string; postcode: string }) => {
      setCustomError(undefined);
      const { data: prepaidOrder } = await instance.catalogues.getPrepaidOrder(
        encodeURIComponent(ref),
        postcode,
      );
      const { data: job } = await instance.jobs.getJobByPostcodeAndRef(
        encodeURIComponent(ref),
        postcode,
      );
      if (job.status !== "Completed")
        throw Error(translate("hwa.errors.skipBagNotCompleted"));
      return prepaidOrder;
    },
    {
      retry: 0,
      onSuccess: (data) => {
        if (
          data.items?.length &&
          data.items[0].sku.includes("PREPAIDCOLLECTION")
        ) {
          context
            .set("refAndPostcode", {
              ref: data.orderRef,
              postcode: data.postcode ?? "",
            })
            .set("products", buildProductMap(data, defaultCatalogue), null)
            .set("bagContents", {
              types: [],
              notes: "",
            })
            .open("bagContents");
        } else {
          setCustomError(translate("hwa.errors.refAndPostcodeFetch"));
        }
      },
    },
  );

  return (
    <View style={{ alignItems: "center" }}>
      <Form state={form} useForm={false}>
        {({ Field }) => (
          <>
            <FlexGrid rowSpacing={null}>
              {isFetching && (
                <Spinner
                  size="large"
                  color="accent"
                  style={{ marginTop: theme.spacing.medium }}
                />
              )}
              {!isFetching && (
                <>
                  {!manualLookup && (
                    <FlexGrid.Cell base="100%">
                      <Text
                        color={Platform.OS === "web" ? "light" : "dark"}
                        styles={{
                          marginHorizontal: "auto",
                          textAlign: "center",
                        }}
                      >
                        {translate(
                          "hwa.screens.selectProduct.selectJobDisclaimer",
                        )}
                      </Text>
                      <Field
                        name="selectedJob"
                        styles={{
                          width: screens.large ? 400 : undefined,
                          marginTop: theme.spacing.medium,
                          marginHorizontal: "auto",
                        }}
                        invertErrorColors
                        errorBackground
                      >
                        {({ field }) => (
                          <>
                            {jobs?.map(({ job }, index) => (
                              <JobSelectionCard
                                key={job.ref}
                                index={index}
                                job={job}
                                active={field.value === index}
                                onPress={() => field.onChange(index)}
                              />
                            ))}
                          </>
                        )}
                      </Field>
                    </FlexGrid.Cell>
                  )}
                  {manualLookup && (
                    <>
                      <FlexGrid.Cell base="50%">
                        <Field
                          name="ref"
                          errorStyles={Platform.select({
                            web: {
                              position: "absolute",
                              bottom: -29,
                            },
                            default: {
                              marginBottom: -theme.spacing.xsmall,
                            },
                          })}
                          labelStyles={{
                            color:
                              Platform.OS === "web"
                                ? theme.colors.light.base
                                : theme.colors.dark.base,
                          }}
                          invertErrorColors
                          errorBackground
                          label={translate("common.ref")}
                        >
                          {({ field }) => (
                            <Input.Text
                              {...field}
                              disabled={isLoading}
                              placeholder={translate(
                                "common.form.placeholder.ref",
                              )}
                            />
                          )}
                        </Field>
                      </FlexGrid.Cell>
                      <FlexGrid.Cell base="50%">
                        <Field
                          name="postcode"
                          errorStyles={Platform.select({
                            web: {
                              position: "absolute",
                              bottom: -29,
                            },
                            default: {
                              marginBottom: -theme.spacing.xsmall,
                            },
                          })}
                          labelStyles={{
                            color:
                              Platform.OS === "web"
                                ? theme.colors.light.base
                                : theme.colors.dark.base,
                          }}
                          invertErrorColors
                          errorBackground
                          label={translate("common.postcode")}
                        >
                          {({ field }) => (
                            <Input.Text
                              {...field}
                              disabled={isLoading}
                              placeholder={translate(
                                "common.form.placeholder.postcode",
                              )}
                            />
                          )}
                        </Field>
                      </FlexGrid.Cell>
                    </>
                  )}
                  {!!jobs?.length && (
                    <FlexGrid.Cell base="100%">
                      <Link
                        underline
                        color={Platform.OS === "web" ? "accent" : "brand"}
                        styles={{ marginTop: theme.spacing.small }}
                        onPress={() => {
                          reset();
                          setCustomError(undefined);
                          setManualLookup(!manualLookup);
                        }}
                      >
                        {!manualLookup && "Switch to manual lookup?"}
                        {manualLookup && "Go Back"}
                      </Link>
                    </FlexGrid.Cell>
                  )}
                </>
              )}
            </FlexGrid>
            {displayErrorMessage(customError, (props) => (
              <View style={{ marginTop: theme.spacing.medium }}>
                <Message
                  {...props}
                  type="error"
                  styles={{
                    backgroundColor: theme.colors.negative.base,
                  }}
                  color="light"
                  background
                >
                  {props.children}
                </Message>
              </View>
            ))}
            {displayErrorMessage(error, (props) => (
              <View style={{ marginTop: theme.spacing.medium }}>
                <Message
                  {...props}
                  type="error"
                  styles={{
                    backgroundColor: theme.colors.negative.base,
                  }}
                  color="light"
                  background
                >
                  {props.children}
                </Message>
              </View>
            ))}
            {!isFetching && (
              <Button
                disabled={isLoading}
                type="button"
                color="warning"
                size="medium"
                styles={{
                  marginTop: theme.spacing.medium,
                  height: 54,
                }}
                onClick={form.submitForm}
              >
                {isLoading ? <Spinner /> : translate("common.continue")}
              </Button>
            )}
          </>
        )}
      </Form>
    </View>
  );
};
